export interface CompressedImage {
  dataUrl: string;
  blob: Blob;
  width: number;
  height: number;
  sizeKB: number;
  originalSizeKB: number;
}

const MAX_WIDTH = 1600;
const MAX_HEIGHT = 2200;
const THUMBNAIL_SIZE = 1200;
const QUALITY = 0.95;

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = reject;
    img.src = url;
  });
}

function drawToCanvas(
  img: HTMLImageElement,
  maxW: number,
  maxH: number
): HTMLCanvasElement {
  let { naturalWidth: w, naturalHeight: h } = img;

  // Smart resize — maintain aspect ratio
  if (w > maxW || h > maxH) {
    const ratio = Math.min(maxW / w, maxH / h);
    w = Math.round(w * ratio);
    h = Math.round(h * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

export async function compressImage(
  file: File,
  isThumbnail = false
): Promise<CompressedImage> {
  const originalSizeKB = Math.round(file.size / 1024);
  const img = await loadImage(file);

  const maxW = isThumbnail ? THUMBNAIL_SIZE : MAX_WIDTH;
  const maxH = isThumbnail ? THUMBNAIL_SIZE : MAX_HEIGHT;
  const canvas = drawToCanvas(img, maxW, maxH);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) { reject(new Error('Compression failed')); return; }
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            dataUrl: reader.result as string,
            blob,
            width: canvas.width,
            height: canvas.height,
            sizeKB: Math.round(blob.size / 1024),
            originalSizeKB,
          });
        };
        reader.readAsDataURL(blob);
      },
      'image/jpeg',
      QUALITY
    );
  });
}

export async function compressAll(files: File[]): Promise<CompressedImage[]> {
  return Promise.all(
    files.map((f, idx) => compressImage(f, idx === 0))
  );
}
